import React, { useEffect, useState } from 'react';
import { getBackendUrl } from '@/config';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2, XCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, failed, expired
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10;

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/dashboard');
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const backendUrl = getBackendUrl();
      const response = await axios.get(
        `${backendUrl}/api/payments/status/${sessionId}`,
        { 
          timeout: 5000,
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        }
      );

      const data = response.data;
      setPaymentDetails(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      } else if (data.payment_status === 'failed') {
        setStatus('failed');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);

    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5 p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center">
            {status === 'checking' && 'Processing Payment...'}
            {status === 'success' && 'Payment Successful!'}
            {status === 'failed' && 'Payment Failed'}
            {status === 'expired' && 'Session Expired'}
            {status === 'timeout' && 'Status Check Timeout'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status Icon */}
          <div className="flex justify-center">
            {status === 'checking' && (
              <Loader2 className="h-16 w-16 text-primary animate-spin" />
            )}
            {status === 'success' && (
              <CheckCircle2 className="h-16 w-16 text-success" />
            )}
            {(status === 'failed' || status === 'expired' || status === 'timeout') && (
              <XCircle className="h-16 w-16 text-destructive" />
            )}
          </div>

          {/* Status Message */}
          <div className="text-center space-y-2">
            {status === 'checking' && (
              <>
                <p className="text-muted-foreground">
                  Please wait while we verify your payment...
                </p>
                <p className="text-sm text-muted-foreground">
                  Attempt {attempts + 1} of {maxAttempts}
                </p>
              </>
            )}
            {status === 'success' && paymentDetails && (
              <>
                <p className="text-lg font-semibold text-foreground">
                  Thank you for your purchase!
                </p>
                <p className="text-muted-foreground">
                  Your payment of ${paymentDetails.amount?.toFixed(2) || '0.00'} {(paymentDetails.currency || 'usd').toUpperCase()} has been processed successfully.
                </p>
              </>
            )}
            {status === 'failed' && (
              <p className="text-muted-foreground">
                Your payment could not be processed. Please try again or contact support.
              </p>
            )}
            {status === 'expired' && (
              <p className="text-muted-foreground">
                Your payment session has expired. Please start a new payment.
              </p>
            )}
            {status === 'timeout' && (
              <p className="text-muted-foreground">
                We're still processing your payment. Please check your email for confirmation or contact support.
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            {status === 'success' && (
              <>
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="w-full"
                >
                  Go to Dashboard
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/payments')}
                  className="w-full"
                >
                  View Payment History
                </Button>
              </>
            )}
            {(status === 'failed' || status === 'expired') && (
              <>
                <Button 
                  onClick={() => navigate('/payments')}
                  className="w-full"
                >
                  Try Again
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="w-full"
                >
                  Back to Dashboard
                </Button>
              </>
            )}
            {status === 'timeout' && (
              <Button 
                onClick={() => navigate('/dashboard')}
                className="w-full"
              >
                Back to Dashboard
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
